import React, { useEffect, useState } from "react";
import Head from "next/head";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument } from 'pdf-lib';
import PDFMerger from "pdf-merger-js/browser";
import { AiOutlineSplitCells, AiOutlineCheck } from "react-icons/ai";
import { FaFileUpload } from "react-icons/fa";
import styles from "../../signature.module.css";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import SignaturePopup from "../../../../components/SignaturePopup";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";


pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const BlogData = [
    {
        id: 1,
        question: "What is PDF split tool?",
        answer: (
            <div className="">
                <p className="">
                    A PDF split tool is a tool that allows you to split a PDF into multiple files. You can use a PDF split tool to extract pages from a PDF or save each page as a separate PDF.
                </p>
            </div>
        ),
    },
    {
        id: 2,
        question: "How to Merge PDFs Online?",
        answer: (
            <div className="">
                <p className="">
                    To merge PDFs online, you need to upload the PDFs you want to merge. Then, you can rearrange the order of the PDFs and download the merged PDF.
                </p>
            </div>
        ),
    },
    {
        id: 3,
        question: "How to Split a PDF into Multiple Files?",
        answer: (
            <div className="">
                <p className="">
                    To split a PDF into multiple files, you need to upload the PDF you want to split. Then, you can select the pages you want to split and download the split PDFs.
                </p>
            </div>
        ),
    },
    {
        id: 4,
        question: "What Other Features Do Online PDF Split Tools Offer?",
        answer: (
            <div className="">
                <p className="">
                    In addition to merging PDFs, online PDF merge tools may offer other features, such as: <br />
                    <ul className="list-disc list-inside">
                        <li>Merging PDFs</li>
                        <li>Compressing PDFs</li>
                        <li>Converting PDFs to other formats</li>
                        <li>Converting other formats to PDFs</li>
                        <li>Rotating PDFs</li>
                    </ul>
                </p>
            </div>
        ),
    },
    {
        id: 5,
        question: "How many PDFs can I split?",
        answer: (
            <div className="">
                <p className="">
                    You can split as many PDFs as you want. There is no limit on the number of PDFs you can split.
                </p>
            </div>
        ),
    },
];

const SplitPdf = () => {
    const [pdfFile, setPdfFile] = useState(null);
    const [numberOfPages, setNumberOfPages] = useState(null);
    const [mergePdf, setMergePdf] = useState(false);
    const [loader, setLoader] = useState(false);
    const [pagesToSplit, setPagesToSplit] = useState({
        individualPages: [],
        pdfFiles: [],
    });
    const [signaturePopup, setSignaturePopup] = useState(false);

    const scrollToPreview = () => {
        const element = document.getElementById('preview-pdfs');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        setPdfFile(file);
        scrollToPreview();
    };

    const handlePdfUpload = (e) => {
        const file = e.target.files[0];
        setPdfFile(file);
        if (file) {
            scrollToPreview();
        }
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumberOfPages(numPages);
        setPagesToSplit({ ...pagesToSplit, individualPages: Array.from(new Array(numPages), (el, index) => index + 1) });
        // console.log(pagesToSplit)
    }

    const splitPdf = async () => {
        setLoader(true);
        if (!pdfFile) return;

        try {
            const pdfBytes = await pdfFile.arrayBuffer();
            const pdfDoc = await PDFDocument.load(pdfBytes);
            const pageCount = pdfDoc.getPageCount();

            if (mergePdf) {
                await splitAndMergePdf(pdfDoc, pageCount);
            } else {
                await splitAndDownloadIndividualPdfs(pdfDoc, pageCount);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const splitAndMergePdf = async (pdfDoc, pageCount) => {
        const pdfFiles = [];

        const promises = pagesToSplit.individualPages.map(async (page) => {
            const newPdf = await PDFDocument.create();
            const [pdfPage] = await newPdf.copyPages(pdfDoc, [page - 1]);
            newPdf.addPage(pdfPage);

            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const file = new File([blob], `page-${page}.pdf`, { type: 'application/pdf' });
            pdfFiles.push(file);
        });

        await Promise.all(promises);

        const merger = new PDFMerger();
        for (const file of pdfFiles) {
            await merger.add(file);
        }

        const mergedPdf = await merger.saveAsBlob();
        setLoader(false);
        downloadPdf(mergedPdf, 'merged.pdf');
    };


    const splitAndDownloadIndividualPdfs = async (pdfDoc, pageCount) => {
        pagesToSplit.individualPages.forEach(async (page) => {
            const newPdf = await PDFDocument.create();
            const [pdfPage] = await newPdf.copyPages(pdfDoc, [page - 1]);
            newPdf.addPage(pdfPage);

            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            downloadPdf(blob, `page-${page}.pdf`);
        });
    };

    const downloadPdf = (blob, fileName) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        setLoader(false);
        setSignaturePopup(true);
    };


    const addOrRemovePage = (pageNumber) => {
        if (pagesToSplit.individualPages.includes(pageNumber)) {
            setPagesToSplit({
                ...pagesToSplit,
                individualPages: pagesToSplit.individualPages.filter((page) => page !== pageNumber),
            });
        } else {
            setPagesToSplit({
                ...pagesToSplit,
                individualPages: [...pagesToSplit.individualPages, pageNumber],
            });
        }
    }

    useEffect(() => {
        setPagesToSplit({ ...pagesToSplit, individualPages: Array.from(new Array(numberOfPages), (el, index) => index + 1), pdfFiles: [] })
    }, [pdfFile]);

    return (
        <div className="w-full mt-20 sm:mt-24">
            <Head>
                <meta charSet="utf-8" />
                <title>Online PDF Split Tool - BoloSign</title>
                <meta name="description" content="Split PDFs into multiple files. Extract pages from your PDF or save each page as a separate PDF." />
                <meta name="keywords" content="pdf split, split pdf, split pdf online, split pdf files, split pdf pages, split pdf into multiple files, split pdf into pages, split pdf into multiple files online, split pdf into pages online, split pdf into multiple files free, split pdf into pages free, split pdf into multiple files free online, split pdf into pages free online, split pdf into multiple files free download, split pdf into pages free download, split pdf into multiple files free software, split pdf into pages free software, split pdf into multiple files free download software, split pdf into pages free download software, split pdf into multiple files free download online, split pdf into pages free download online, split pdf into multiple files free download for windows 10, split pdf into pages free download for windows 10, split pdf into multiple files free download for windows 7, split pdf into pages free download for windows 7" />
                <meta name="author" content="BoloSign" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="canonical" href="https://www.bolosign.com/signature/pdf-tools/split-pdf" />
                <meta property="og:title" content="Online PDF Split Tool - BoloSign" />
                <meta property="og:description" content="Split PDFs into multiple files. Extract pages from your PDF or save each page as a separate PDF." />
                <meta property="og:url" content="https://www.bolosign.com/signature/pdf-tools/split-pdf" />
                <meta property="og:site_name" content="BoloSign" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content="BoloSign" />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en_US" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@BoloForms" />
                <meta name="twitter:creator" content="@BoloForms" />
                <meta name="twitter:title" content="Online PDF Split Tool - BoloForms" />
                <meta name="twitter:description" content="Split PDFs into multiple files. Extract pages from your PDF or save each page as a separate PDF." />
            </Head>

            <div className="">
                <div className="max-w-[1280px] mx-auto px-2 md:px-10 py-20 rounded-xl relative flex flex-col gap-20">
                    <div className="text-violet-500 text-center">
                        <h1 className="text-3xl md:text-6xl text-gray-800">
                            Online PDF Split Tool
                        </h1>
                        <p className="text-lg md:text-2xl mt-4 text-gray-600">
                            Split PDFs into multiple files. Extract pages from your PDF or save each page as a separate PDF.
                        </p>
                    </div>
                    <div
                        className="extraOutline p-4 bg-white bg-whtie mx-auto rounded-lg w-full md:w-[600px]"
                        onDragEnter={(e) => e.preventDefault()}
                        onDragOver={(e) => e.preventDefault()}
                        onDragLeave={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                    >
                        <div className="file_upload px-2 md:px-20 py-5 md:py-14 relative border-4 border-dotted border-gray-300 rounded-lg">

                            <FaFileUpload className="text-gray-400 w-20 md:w-32 mx-auto mb-6 md:mb-10 text-[80px] md:text-[120px]" />
                            <div className="input_field flex flex-col w-full mx-auto text-center">
                                <label>
                                    <input
                                        onChange={handlePdfUpload}
                                        className="text-sm cursor-pointer w-36 hidden"
                                        type="file"
                                        accept=".pdf"
                                    />
                                    <div className="text bg-violet-600 text-white text-xl border border-gray-300 rounded-xl font-semibold cursor-pointer py-3 md:py-4 hover:bg-indigo-500">
                                        Upload PDF File
                                    </div>
                                </label>

                                <div className="title text-gray-500 uppercase text-lg md:text-xl">
                                    or drop file here
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="preview-pdfs" className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center">
                    {pdfFile && (
                        <>
                            <h1 className="text-3xl font-light text-center text-gray-700">
                                Disselect Pages that you {`don't`} want
                            </h1>

                            <div
                                className="w-full flex flex-wrap justify-center gap-10 md:border-2 md:px-10 md:py-14 md:rounded-xl"
                            >
                                {
                                    Array.from(new Array(numberOfPages), (el, index) =>
                                    (
                                        <div
                                            key={index}
                                            className="border drop-shadow-2xl w-fit relative"
                                        >
                                            <Document file={pdfFile} onLoadSuccess={onDocumentLoadSuccess}>
                                                <Page
                                                    pageNumber={index + 1}
                                                    width={260}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={false}
                                                />
                                            </Document>
                                            <div className="absolute top-2 left-2 w-8 h-8 bg-violet-500 rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                                                {index + 1}
                                            </div>
                                            <div onClick={() => addOrRemovePage(index + 1)} className={`absolute top-2 right-2 w-8 h-8 ${pagesToSplit.individualPages.includes(index + 1) ? "bg-violet-500" : "bg-gray-400"} rounded-full text-xl text-white flex justify-center items-center cursor-pointer`}>
                                                <AiOutlineCheck />
                                            </div>
                                        </div>

                                    ))
                                }
                            </div>

                        </>
                    )}
                    {pdfFile && (
                        <div id="split-pdfs" className="w-full">
                            <div className="flex justify-center items-center gap-2 mb-4">
                                <input
                                    onChange={(e) => setMergePdf(e.target.checked)}
                                    className="w-5 h-5" type="checkbox" name="" id="" />
                                <label htmlFor="" className="text-gray-600 text-xl">
                                    Merge Selected PDFs into a single PDF
                                </label>
                            </div>
                            <button
                                onClick={splitPdf}
                                className="bg-violet-600 mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400"
                            >
                                {loader ? (
                                    <div
                                        class={`flex items-center text-white space-x-2 ${styles.loader}`}
                                    >
                                        <div class="">Downloading</div>
                                        <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                        <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                        <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                    </div>
                                ) : (
                                    <>
                                        Confirm to Split <AiOutlineSplitCells className="text-3xl" />
                                    </>
                                )}
                            </button>
                            {/* {mergedPdfUrl && (
                                <div className="w-full flex flex-col gap-3 justify-center items-center">
                                    <div className="w-[2px] h-10 bg-gray-300 mt-2"></div>
                                    <p className="text-violet-600 text-center text-xl">
                                        Pdf Files Merged Successfully
                                    </p>
                                    <div className="w-[2px] h-10 bg-gray-300"></div>
                                    <button
                                        // onClick={downloadMergedPdf}
                                        className="bg-violet-600 w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400 mx-auto"
                                    >
                                        Download Merged Pdf
                                    </button>
                                </div>
                            )} */}
                        </div>
                    )}
                </div>

                {
                    signaturePopup && <SignaturePopup onPopupClose={() => setSignaturePopup(false)} source="SPLIT_PDF" />
                }

                <div className="mt-20">
                    <div className="mb-20">
                        <PdfToolsLinks />
                    </div>
                    <Blog heading="PDF Split Tool FAQs" BlogData={BlogData} />
                </div>
            </div>
        </div>
    );
};

export default SplitPdf;
